import React, { useEffect, useMemo, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useTranslation } from 'react-i18next';
import { SButton } from '@/components/ui/atoms/SButton';
import SLoader from '@/components/ui/atoms/SLoader';
import { StripeService } from '@/services/stripe.service';
import { logger } from '@/utils/logger';
import './UpdatePaymentMethodForm.css';

/**
 * Amount authorised when a member updates their payment method. The backend's
 * create-payment-intent call (the same call the booking/shop payment flow uses)
 * sets `setup_future_usage: 'off_session'`, so confirming this intent both
 * validates the card and saves it to the member's Stripe customer for the
 * outstanding membership charge.
 */
const CARD_UPDATE_AMOUNT_CENTS = 100;

interface UpdatePaymentMethodFormProps {
  readonly memberId: string;
  readonly locationId: string;
  readonly email?: string;
  readonly onSuccess: (externalPaymentId: string) => void;
}

function isDarkMode(): boolean {
  return document.documentElement.classList.contains('dark');
}

function cssVar(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

interface CardFormProps {
  readonly onSuccess: (externalPaymentId: string) => void;
  readonly email?: string;
}

const CardForm: React.FC<CardFormProps> = ({ onSuccess, email }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    setErrorMsg('');

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: email ? { receipt_email: email } : {},
      redirect: 'if_required',
    });

    if (error) {
      setErrorMsg(error.message || t('membership.pastDue.form.genericError'));
      setIsSubmitting(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      onSuccess(paymentIntent.id);
      return;
    }

    setErrorMsg(t('membership.pastDue.form.genericError'));
    setIsSubmitting(false);
  };

  return (
    <form className="upm-form" onSubmit={handleSubmit}>
      <div className="upm-form__card">
        <PaymentElement options={{ layout: 'tabs' }} />

        {errorMsg && <p className="upm-form__error">{errorMsg}</p>}
      </div>

      {/* Sticky footer — stays reachable while the Stripe fields scroll */}
      <div className="upm-form__footer">
        <SButton
          size="xl"
          variant={!stripe || isSubmitting ? 'disabled' : 'primary'}
          className="upm-form__submit"
          label={
            isSubmitting
              ? t('membership.pastDue.form.submitting')
              : t('membership.pastDue.form.submit')
          }
          onClick={handleSubmit as unknown as React.MouseEventHandler<HTMLButtonElement>}
        />

        <p className="upm-form__powered">{t('membership.pastDue.form.poweredBy')}</p>
      </div>
    </form>
  );
};

export const UpdatePaymentMethodForm: React.FC<UpdatePaymentMethodFormProps> = ({
  memberId,
  locationId,
  email,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const initialise = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const config = await StripeService.checkConfiguration(locationId);
        if (cancelled) return;

        if (!config.isConfigured) {
          setLoadError(t('membership.pastDue.form.notConfigured'));
          setIsLoading(false);
          return;
        }

        // Same call the existing payment flow uses to add a card.
        const intent = await StripeService.createPaymentIntent({
          amount: CARD_UPDATE_AMOUNT_CENTS,
          currency: 'usd',
          memberId,
          locationId,
          // Tags the charge so the Box OS transaction log shows it as a card
          // verification rather than a mystery $1 of revenue.
          purpose: 'card_update',
        });
        if (cancelled) return;

        setClientSecret(intent.clientSecret);
        setPublishableKey(intent.publishableKey);
      } catch (err) {
        logger.error('Failed to initialise payment method update:', err);
        if (!cancelled) setLoadError(t('membership.pastDue.form.loadError'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    initialise();

    return () => {
      cancelled = true;
    };
  }, [memberId, locationId, t]);

  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey],
  );

  const appearance = useMemo(
    () => ({
      theme: (isDarkMode() ? 'night' : 'stripe') as 'night' | 'stripe',
      variables: {
        colorPrimary: cssVar('--tbx-gold', '#c99535'),
        colorBackground: cssVar('--tbx-card', '#ffffff'),
        colorText: cssVar('--tbx-text', '#0b0b0a'),
        colorTextSecondary: cssVar('--tbx-text-sec', '#6b6860'),
        colorDanger: '#ef4444',
        borderRadius: '10px',
        fontFamily: cssVar('--Font-Family-Body', 'Inter, system-ui, sans-serif'),
        fontSizeBase: '15px',
      },
    }),
    [publishableKey],
  );

  if (isLoading) {
    return (
      <div className="upm-state">
        <SLoader />
        <p className="upm-state__text">{t('membership.pastDue.form.loading')}</p>
      </div>
    );
  }

  if (loadError || !clientSecret || !stripePromise) {
    return (
      <div className="upm-state">
        <p className="upm-state__error">
          {loadError ?? t('membership.pastDue.form.loadError')}
        </p>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance } as any}>
      <CardForm onSuccess={onSuccess} email={email} />
    </Elements>
  );
};

export default UpdatePaymentMethodForm;
